import {
  CellContext,
  Row as TanTableRow,
  createColumnHelper,
} from "@tanstack/react-table";
import { FormEvent, useCallback, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, InputGroup } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Loading from "../Loading";
import { RowExpander } from "../components/Table/RowExpander";
import { Table, metaDefault, useTable } from "../components/Table/Table";
import { formatCurrency } from "../lib/numbers";
import {
  RebalanceRow,
  getRealizedGainLoss,
  getRiskAllocationDisplay,
  mapRebalancesToRebalanceRows,
  rebalanceTypeDisplay,
} from "../rebalances/lib";
import { statusLabel, statusVariant } from "../rebalances/RebalanceCheckPanel";
import {
  DEFAULT_ENABLED_SEVERITIES,
  DEFAULT_SCORE_UNDER,
  SEVERITIES,
  Severity,
  composeAdminQuery,
} from "./adminRebalanceQuery";
import { useAdminFirms } from "./useAdminFirms";
import { useAdminRebalanceList } from "./useAdminRebalanceList";

const columnHelper = createColumnHelper<RebalanceRow>();

// The Spec-4 read-only Explain viewer for a rebalance (R21/AC13). Single source
// of truth so the household-name <Link> and the whole-row click target can't
// drift apart.
const explainPath = (rebalanceId: number) =>
  `/admin/rebalances/${rebalanceId}/explain`;

// APP-1282: badge bg per severity for the admin list's count chips, mirroring
// RebalanceCheckPanel (critical/error→danger, warning→warning, info→info).
const SEVERITY_BADGE_BG: Record<Severity, string> = {
  critical: "danger",
  error: "danger",
  warning: "warning",
  info: "info",
};

/**
 * APP-1282 (R10/R11/R12/R19): the check column for a household row. Renders the
 * score pill + status badge in the action_required→danger / review_recommended
 * →warning / pass→success variant, and the four severity counts as badges. An
 * UNCHECKED rebalance (null check_status) shows a distinct neutral "Unchecked"
 * indicator — NOT a passing check. Visible by default on initial render.
 */
function CheckCell({ row }: { row: RebalanceRow }) {
  if (row.level !== "household") return null;

  const status = row.checkStatus ?? null;
  // Unchecked: no rebalance_check row → all six fields null. Distinct from pass.
  if (status == null) {
    return (
      <div className="d-flex align-items-center gap-1 flex-wrap">
        <Badge bg="light" text="dark" className="border" aria-label="Unchecked">
          Unchecked
        </Badge>
        <AuditSeverityBadge row={row} />
      </div>
    );
  }

  const variant = statusVariant(status);
  return (
    <div className="d-flex align-items-center gap-1 flex-wrap">
      <Badge bg={variant} aria-label={`Check score ${row.checkScore}`}>
        {row.checkScore}/100
      </Badge>
      <Badge bg={variant}>{statusLabel(status)}</Badge>
      <AuditSeverityBadge row={row} />
      {SEVERITIES.map((sev) => {
        const count = (row[
          `check${sev[0].toUpperCase()}${sev.slice(1)}` as
            "checkCritical" | "checkError" | "checkWarning" | "checkInfo"
        ] ?? 0) as number;
        if (!count) return null;
        return (
          <Badge
            key={sev}
            bg={SEVERITY_BADGE_BG[sev]}
            aria-label={`${count} ${sev}`}
          >
            {count} {sev}
          </Badge>
        );
      })}
    </div>
  );
}

/**
 * APP-1168 (s-rebalance-audit-analysis-pipeline-019eb94b D8): the audit-severity
 * badge for a household row, driven by the `rebalance_audit.max_severity`
 * pointer (`row.auditMaxSeverity`). Composes alongside the existing check
 * badges; renders nothing when the rebalance has no audit pointer (un-audited)
 * so it never breaks the row's deep-link / row-click affordance.
 */
function AuditSeverityBadge({ row }: { row: RebalanceRow }) {
  const severity = row.auditMaxSeverity ?? null;
  if (severity == null) return null;
  const variant = SEVERITY_BADGE_BG[severity];
  return (
    <Badge bg={variant} aria-label={`Audit ${severity}`}>
      Audit: {severity.toUpperCase()}
    </Badge>
  );
}

/**
 * Read-only rebalance list columns for the staff admin view
 * (s-firm-scoped-admin-proposal-019ea1d9 R19). MIRRORS the advisor
 * `RebalancesApprovedTable` data columns (Household / Reason / allocations /
 * AUM / Cash / Buy / Sell / Net / Realized G/L) but DROPS the select checkbox
 * and the per-row action buttons — the admin view renders NO mutating control
 * (R22/AC12). The Household cell deep-links to the Spec-4 read-only Explain
 * viewer at `/admin/rebalances/:rebalanceId/explain` (R21/AC13).
 */
// Household (top-level) rows deep-link to the Spec-4 read-only Explain viewer
// (R21/AC13); account sub-rows are plain labels. Same depth-indented wrapper
// either way — only the household variant wraps the value in the <Link>.
// APP-2137 (R39/R72): a household row with accounts draws a RowExpander before
// the link, closed on each page read; opening it shows one account row per
// account below it. TanStack resets the expanded state on each new page read.
// The expander is a <button>, so the row click guard leaves it to toggle
// rather than navigate.
const renderNameCell = (info: CellContext<RebalanceRow, string>) => (
  <div style={{ paddingLeft: `${info.row.depth * 2}rem` }}>
    {info.row.original.level === "household" ? (
      <>
        {info.row.getCanExpand() ? <RowExpander row={info.row} /> : ""}{" "}
        <Link to={explainPath(info.row.original.rebalanceId)}>
          {info.getValue()}
        </Link>
      </>
    ) : (
      info.getValue()
    )}
  </div>
);

const adminColumns = [
  columnHelper.accessor("name", {
    // APP-1289: the list sets `getSubRows`, so every household row has account
    // sub-rows and TanStack treats this cell as AGGREGATED even with grouping
    // disabled. The shared Table renders aggregated cells via
    // `aggregatedCell ?? cell`, and TanStack auto-injects a default
    // `aggregatedCell` that renders the bare value — silently dropping our link.
    // Wiring the SAME renderer into BOTH keeps the deep-link present whether or
    // not the household has accounts (regression on APP-1282).
    cell: renderNameCell,
    aggregatedCell: renderNameCell,
    header: () => "Household",
    minSize: 225,
  }),
  columnHelper.accessor(
    (row: RebalanceRow) =>
      row.level === "account" || typeof row.type === "undefined"
        ? ""
        : (rebalanceTypeDisplay[
            row.type as keyof typeof rebalanceTypeDisplay
          ] ?? ""),
    {
      id: "type",
      header: () => "Trade Proposal Reason",
      enableColumnFilter: false,
    },
  ),
  // APP-1282 (R10/R11/R12/R19): check score + severity + unchecked indicator,
  // visible by default. Display-only — uses the accessor purely so the column is
  // present; the cell renders the score pill / status / counts (or "Unchecked").
  columnHelper.accessor((row) => row.checkScore ?? null, {
    id: "check",
    // APP-1289: household rows are aggregated (every household has account
    // sub-rows via getSubRows), so — like the name column — the custom `cell`
    // must also be wired into `aggregatedCell`, or the auto-injected default
    // renders the bare checkScore (or nothing when unchecked) instead of the
    // score pill / status / severity counts / "Unchecked" indicator.
    cell: (info) => <CheckCell row={info.row.original} />,
    aggregatedCell: (info) => <CheckCell row={info.row.original} />,
    header: () => "Checks",
    enableColumnFilter: false,
    enableSorting: false,
    meta: { className: "text-nowrap" },
  }),
  columnHelper.accessor((row) => row.riskAllocation?.current.growth ?? 0, {
    id: "current",
    cell: (info) =>
      getRiskAllocationDisplay(info.row.original.riskAllocation?.current),
    aggregatedCell: (info) =>
      getRiskAllocationDisplay(info.row.original.riskAllocation?.current),
    header: () => "Current Allocation",
    enableColumnFilter: false,
    enableSorting: false,
    meta: { className: "text-nowrap" },
  }),
  columnHelper.accessor(
    (row) => row.riskAllocation?.postRebalance.growth ?? 0,
    {
      id: "postRebalance",
      cell: (info) =>
        getRiskAllocationDisplay(
          info.row.original.riskAllocation?.postRebalance,
        ),
      aggregatedCell: (info) =>
        getRiskAllocationDisplay(
          info.row.original.riskAllocation?.postRebalance,
        ),
      header: () => "Post-Trade Proposal Allocation",
      enableColumnFilter: false,
      enableSorting: false,
      meta: { className: "text-nowrap" },
    },
  ),
  columnHelper.accessor((row) => row.riskAllocation?.target.growth ?? 0, {
    id: "target",
    cell: (info) =>
      getRiskAllocationDisplay(info.row.original.riskAllocation?.target),
    aggregatedCell: (info) =>
      getRiskAllocationDisplay(info.row.original.riskAllocation?.target),
    header: () => "Target Allocation",
    enableColumnFilter: false,
    enableSorting: false,
    meta: { className: "text-nowrap" },
  }),
  columnHelper.accessor((row) => row.totalBalance ?? 0, {
    id: "totalBalance",
    cell: (info) => formatCurrency(info.row.original.totalBalance),
    aggregatedCell: (info) => formatCurrency(info.row.original.totalBalance),
    header: () => "AUM",
    enableColumnFilter: false,
    meta: { headerClassName: "text-end", className: "text-end" },
  }),
  columnHelper.accessor((row) => row.cashBalance ?? 0, {
    id: "cashBalance",
    cell: (info) => formatCurrency(info.row.original.cashBalance),
    aggregatedCell: (info) => formatCurrency(info.row.original.cashBalance),
    header: () => "Cash",
    enableColumnFilter: false,
    meta: { headerClassName: "text-end", className: "text-end" },
  }),
  columnHelper.accessor((row) => row.buyTotal ?? 0, {
    id: "buyTotal",
    cell: (info) => formatCurrency(info.getValue()),
    aggregatedCell: (info) => formatCurrency(info.getValue()),
    header: () => "Buy",
    enableColumnFilter: false,
    meta: { headerClassName: "text-end", className: "text-end" },
  }),
  columnHelper.accessor((row) => row.sellTotal ?? 0, {
    id: "sellTotal",
    cell: (info) => formatCurrency(info.getValue()),
    aggregatedCell: (info) => formatCurrency(info.getValue()),
    header: () => "Sell",
    enableColumnFilter: false,
    meta: { headerClassName: "text-end", className: "text-end" },
  }),
  columnHelper.accessor((row) => (row.sellTotal ?? 0) - (row.buyTotal ?? 0), {
    id: "net",
    cell: (info) => formatCurrency(info.getValue()),
    aggregatedCell: (info) => formatCurrency(info.getValue()),
    header: () => "Net",
    enableColumnFilter: false,
    meta: { headerClassName: "text-end", className: "text-end" },
  }),
  columnHelper.accessor((row) => getRealizedGainLoss(row) ?? 0, {
    id: "realizedGainLoss",
    cell: (info) => formatCurrency(getRealizedGainLoss(info.row.original)),
    aggregatedCell: (info) =>
      formatCurrency(getRealizedGainLoss(info.row.original)),
    header: () => "Realized Gain/Loss",
    enableColumnFilter: false,
    meta: { headerClassName: "text-end", className: "text-end" },
  }),
];

/**
 * Resolve a raw URL `firmId` to a positive integer, or `undefined` when it is
 * absent / non-numeric / non-positive (R17/AC19): an invalid URL `firmId` falls
 * back to the no-firm-selected prompt (R23).
 */
export function parseFirmId(raw: string | null): number | undefined {
  if (raw == null || !/^\d+$/.test(raw.trim())) return undefined;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : undefined;
}

/**
 * Staff-only, firm-scoped, read-only rebalance list
 * (s-firm-scoped-admin-proposal-019ea1d9). Mounted at `/admin/rebalances`
 * behind the Spec-1 `StaffRoute` (R15). A staff user picks a firm and
 * browses/server-side-filters that firm's rebalances read-only, then drills
 * into the Spec-4 read-only Explain (R21).
 *
 * State machine (R23–R28): no-firm prompt (0 list requests) → loading → exactly
 * one of results · empty-state · invalid-RQL (message + retained query) ·
 * non-400 error (with retry). The firm selector has its own empty/error state
 * (R28). `firmId` + `q` persist in the URL (R29); changing the firm resets the
 * page and preserves `q` (R18). Renders NO board control (R20/AC22) and NO
 * mutating control (R22/AC12).
 */
export default function AdminRebalanceList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL-seeded state (R17/R29): a valid `firmId` seeds the selector + one list
  // request; an invalid one falls back to the no-firm prompt.
  const firmId = parseFirmId(searchParams.get("firmId"));
  const urlQuery = searchParams.get("q") ?? "";

  // The query input is uncommitted text; the active `q` is the URL value. A
  // local draft lets the user type without firing a request per keystroke; the
  // submit commits it to the URL (R29).
  const [draftQuery, setDraftQuery] = useState(urlQuery);

  // APP-1282 exception-triage refinements (R13–R18). All inactive by default so
  // the toggle alone defines the default exception view (R14) and the initial
  // render imposes no check clause.
  const [exceptionsOnly, setExceptionsOnly] = useState(false);
  const [chipsEngaged, setChipsEngaged] = useState(false);
  const [enabledSeverities, setEnabledSeverities] = useState<
    Record<Severity, boolean>
  >({ ...DEFAULT_ENABLED_SEVERITIES });
  const [scoreUnderEngaged, setScoreUnderEngaged] = useState(false);
  // APP-2138/R33: the field keeps the typed text; an empty text is `null` and
  // adds no score-under term.
  const [scoreUnderText, setScoreUnderText] = useState(
    String(DEFAULT_SCORE_UNDER),
  );
  const scoreUnder =
    scoreUnderText.trim() === "" ? null : Number(scoreUnderText);

  // R18: the active `q` (committed manual query from the URL) AND-composes with
  // every active refinement. This composed string is what the server-side RQL
  // engine executes (R9) — there is no client-side filtering.
  const composedQuery = useMemo(
    () =>
      composeAdminQuery({
        manualQuery: urlQuery,
        exceptionsOnly,
        chipsEngaged,
        enabledSeverities,
        scoreUnderEngaged,
        scoreUnder,
      }),
    [
      urlQuery,
      exceptionsOnly,
      chipsEngaged,
      enabledSeverities,
      scoreUnderEngaged,
      scoreUnder,
    ],
  );

  const { status: firmsStatus, firms } = useAdminFirms();
  // R67/R68: the page resets to 1 when the firm or the composed query changes.
  // The page is stored with the key it was chosen under. A new key stores page
  // 1 under that key during this render, so a return to an earlier firm or
  // query also reads page 1, not the page chosen before.
  const pageKey = `${firmId ?? ""}|${composedQuery}`;
  const [pageState, setPageState] = useState({ key: pageKey, page: 1 });
  if (pageState.key !== pageKey) setPageState({ key: pageKey, page: 1 });
  const pageNumber = pageState.key === pageKey ? pageState.page : 1;
  const goToPage = useCallback(
    (next: number) => setPageState({ key: pageKey, page: next }),
    [pageKey],
  );

  const { status, page, invalidRqlMessage, retry } = useAdminRebalanceList({
    firmId,
    q: composedQuery,
    page: pageNumber,
    // APP-2140/R60/R70: no list read while the firm list runs, after it fails,
    // or when it returns no firm, also for a valid `firmId` of the URL.
    enabled: firmsStatus === "ok" && firms.length > 0,
  });

  // R18: changing the firm resets `page` (page is not URL-persisted, so a fresh
  // selection naturally starts at page 1) and PRESERVES `q`; exactly one list
  // request fires against the new firm via the changed query key.
  const onFirmChange = useCallback(
    (next: string) => {
      const nextParams = new URLSearchParams(searchParams);
      const id = parseFirmId(next);
      if (id == null) {
        nextParams.delete("firmId");
      } else {
        nextParams.set("firmId", String(id));
      }
      // Preserve `q` (do not touch it); page is not persisted, so it resets.
      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams],
  );

  const onSubmitQuery = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const nextParams = new URLSearchParams(searchParams);
      if (draftQuery.trim()) {
        nextParams.set("q", draftQuery);
      } else {
        nextParams.delete("q");
      }
      setSearchParams(nextParams);
    },
    [draftQuery, searchParams, setSearchParams],
  );

  const toggleSeverity = useCallback((sev: Severity) => {
    setEnabledSeverities((prev) => ({ ...prev, [sev]: !prev[sev] }));
  }, []);

  const rebalanceRows = useMemo(
    () => mapRebalancesToRebalanceRows(page?.rebalances),
    [page],
  );

  // APP-2139 / R37: the table shows no Service Tier column, also while the staff
  // user's profile has `connections.slant` true; `adminColumns` are used as they are.
  const columns = adminColumns;

  const { table } = useTable({
    columns,
    data: rebalanceRows,
    getRowId: (row) => `${row.rebalanceId}-${row.level}-${row.id}`,
    getSubRows: (row) => row.accounts ?? [],
    // The server pages the list; the table shows every row of the page read.
    manualPagination: true,
    meta: { ...metaDefault },
  });

  // APP-1289: make the WHOLE household row a click target for the read-only
  // Explain viewer — a mouse convenience on top of the canonical name <Link>.
  // Only household rows navigate; account sub-rows aren't a rebalance, so they
  // return no handler (not clickable). The shared Table applies the pointer
  // cursor and the click guard (`shouldIgnoreRowClick`), so this stays a
  // one-liner instead of a copied row renderer.
  const rowClickHandler = useCallback(
    (row: TanTableRow<RebalanceRow>) =>
      row.original.level === "household"
        ? () => navigate(explainPath(row.original.rebalanceId))
        : undefined,
    [navigate],
  );

  return (
    <section aria-label="Admin rebalance list">
      <h2 className="h5">Firm rebalances</h2>

      <FirmSelector
        firmsStatus={firmsStatus}
        firms={firms}
        selectedFirmId={firmId}
        onChange={onFirmChange}
      />

      {/* RQL query control (R19) — a plain text input only. No board selector /
          board-management control is rendered (R20/AC22). */}
      <Form onSubmit={onSubmitQuery} className="my-3" noValidate>
        <InputGroup>
          <Form.Control
            type="text"
            aria-label="Filter query"
            placeholder="Filter (e.g. num_trades>5)"
            value={draftQuery}
            onChange={(e) => setDraftQuery(e.target.value)}
            disabled={firmId == null}
          />
          <Button type="submit" variant="secondary" disabled={firmId == null}>
            Apply filter
          </Button>
        </InputGroup>
      </Form>

      {/* APP-1282 exception-triage controls (R13–R18). The toggle is the
          canonical exception lens; the chips + score-under are optional,
          inactive-by-default refinements. All read-only (no mutating request;
          they only recompose the GET query). */}
      <ExceptionControls
        disabled={firmId == null}
        exceptionsOnly={exceptionsOnly}
        onToggleExceptions={() => setExceptionsOnly((v) => !v)}
        chipsEngaged={chipsEngaged}
        onToggleChips={() => setChipsEngaged((v) => !v)}
        enabledSeverities={enabledSeverities}
        onToggleSeverity={toggleSeverity}
        scoreUnderEngaged={scoreUnderEngaged}
        onToggleScoreUnder={() => setScoreUnderEngaged((v) => !v)}
        scoreUnder={scoreUnderText}
        onScoreUnderChange={setScoreUnderText}
      />

      <ListBody
        firmSelected={firmId != null}
        status={status}
        hasRows={rebalanceRows.length > 0}
        invalidRqlMessage={invalidRqlMessage}
        retry={retry}
        table={table}
        rowClickHandler={rowClickHandler}
        pageNumber={pageNumber}
        hasMore={page?.hasMore ?? false}
        onPageChange={goToPage}
      />
    </section>
  );
}

/**
 * APP-1282 exception-triage controls. Read-only: none of these issue a request
 * directly — they recompose the GET query the list already fires (R20/AC17).
 *
 *  - "Exceptions only" (R13): the canonical lens `check_status != 'pass'`.
 *  - Severity chips (R15/R16): an "Engage" switch + per-severity toggles;
 *    inactive by default, critical/error/warning enabled / info disabled when
 *    first engaged.
 *  - Score under N (R17): an "Engage" switch + threshold input; inactive by
 *    default, initial N = 90, strict `<`.
 */
function ExceptionControls({
  disabled,
  exceptionsOnly,
  onToggleExceptions,
  chipsEngaged,
  onToggleChips,
  enabledSeverities,
  onToggleSeverity,
  scoreUnderEngaged,
  onToggleScoreUnder,
  scoreUnder,
  onScoreUnderChange,
}: {
  disabled: boolean;
  exceptionsOnly: boolean;
  onToggleExceptions: () => void;
  chipsEngaged: boolean;
  onToggleChips: () => void;
  enabledSeverities: Record<Severity, boolean>;
  onToggleSeverity: (sev: Severity) => void;
  scoreUnderEngaged: boolean;
  onToggleScoreUnder: () => void;
  scoreUnder: string;
  onScoreUnderChange: (text: string) => void;
}) {
  return (
    <div
      className="d-flex flex-wrap align-items-center gap-3 mb-3"
      aria-label="Exception triage controls"
    >
      <Form.Check
        type="switch"
        id="admin-exceptions-only"
        label="Exceptions only"
        checked={exceptionsOnly}
        disabled={disabled}
        onChange={onToggleExceptions}
      />

      <div className="d-flex align-items-center gap-2">
        <Form.Check
          type="switch"
          id="admin-severity-chips"
          label="Severity"
          checked={chipsEngaged}
          disabled={disabled}
          onChange={onToggleChips}
        />
        {SEVERITIES.map((sev) => (
          <Button
            key={sev}
            type="button"
            size="sm"
            variant={
              chipsEngaged && enabledSeverities[sev]
                ? SEVERITY_BADGE_BG[sev]
                : "outline-secondary"
            }
            aria-pressed={chipsEngaged && enabledSeverities[sev]}
            disabled={disabled || !chipsEngaged}
            onClick={() => onToggleSeverity(sev)}
          >
            {sev}
          </Button>
        ))}
      </div>

      <div className="d-flex align-items-center gap-2">
        <Form.Check
          type="switch"
          id="admin-score-under"
          label="Score under"
          checked={scoreUnderEngaged}
          disabled={disabled}
          onChange={onToggleScoreUnder}
        />
        <Form.Control
          type="number"
          aria-label="Score under threshold"
          style={{ width: "5rem" }}
          value={scoreUnder}
          disabled={disabled || !scoreUnderEngaged}
          onChange={(e) => onScoreUnderChange(e.target.value)}
        />
      </div>
    </div>
  );
}

function FirmSelector({
  firmsStatus,
  firms,
  selectedFirmId,
  onChange,
}: {
  firmsStatus: ReturnType<typeof useAdminFirms>["status"];
  firms: ReturnType<typeof useAdminFirms>["firms"];
  selectedFirmId: number | undefined;
  onChange: (next: string) => void;
}) {
  if (firmsStatus === "loading") {
    return <Loading message="firms" />;
  }
  // R28/AC21: firm-list failure → distinct error state; the page issues no
  // list request (the selector renders no options, so no firm can be chosen).
  if (firmsStatus === "error") {
    return (
      <Alert variant="danger" role="alert">
        Failed to load the firm list.
      </Alert>
    );
  }
  // R28/AC21: 0 firms → distinct empty state; no list request.
  if (firms.length === 0) {
    return (
      <Alert variant="warning" role="alert">
        No firms are available.
      </Alert>
    );
  }

  return (
    <Form.Group controlId="admin-firm-selector">
      <Form.Label>Firm</Form.Label>
      <Form.Select
        aria-label="Firm"
        value={selectedFirmId == null ? "" : String(selectedFirmId)}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Select a firm…</option>
        {firms.map((firm) => (
          <option key={firm.id} value={String(firm.id)}>
            {firm.name}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  );
}

function ListBody({
  firmSelected,
  status,
  hasRows,
  invalidRqlMessage,
  retry,
  table,
  rowClickHandler,
  pageNumber,
  hasMore,
  onPageChange,
}: {
  firmSelected: boolean;
  status: ReturnType<typeof useAdminRebalanceList>["status"];
  hasRows: boolean;
  invalidRqlMessage: string | undefined;
  retry: () => void;
  table: ReturnType<typeof useTable<RebalanceRow>>["table"];
  rowClickHandler: (row: TanTableRow<RebalanceRow>) => (() => void) | undefined;
  pageNumber: number;
  hasMore: boolean;
  onPageChange: (page: number) => void;
}) {
  const pager = (
    <AdminPager
      pageNumber={pageNumber}
      hasMore={hasMore}
      onPageChange={onPageChange}
    />
  );

  // R23/AC14: no firm selected → firm-selection prompt; 0 list requests (the
  // hook is disabled, so `status` is `idle`).
  if (!firmSelected || status === "idle") {
    return (
      <Alert variant="info" role="status">
        Select a firm to view its rebalances.
      </Alert>
    );
  }

  // R24/AC17: loading indicator; suppress empty + stale results.
  if (status === "loading") {
    return <Loading message="rebalances" />;
  }

  // R27/AC7: invalid RQL 400 — show the server message; the entered query text
  // is retained (it lives in the URL + the input's draft state, untouched).
  if (status === "invalid-rql") {
    return (
      <Alert variant="danger" role="alert">
        {invalidRqlMessage ?? "Invalid filter query."}
      </Alert>
    );
  }

  // R26/AC16: non-400 failure → distinct error state WITH a retry affordance.
  if (status === "error") {
    return (
      <Alert variant="danger" role="alert">
        Failed to load rebalances.{" "}
        <Button variant="link" className="p-0 align-baseline" onClick={retry}>
          Retry
        </Button>
      </Alert>
    );
  }

  // R25/AC15: 0 rebalances → empty state, NOT an error.
  if (!hasRows) {
    // R69: past page 1 an empty read keeps the pager so the user can go back.
    if (pageNumber > 1) return pager;
    return (
      <Alert variant="secondary" role="status">
        This firm has no rebalances.
      </Alert>
    );
  }

  // Results. The whole-household-row click affordance is provided by the shared
  // Table via `rowClickHandler` (APP-1289) — no custom RowComp needed.
  return (
    <>
      <Table
        table={table}
        rowClickHandler={rowClickHandler}
        showPager={false}
      />
      {pager}
    </>
  );
}

// R61–R66: the server-paged pager over `page` and `hasMore`.
function AdminPager({
  pageNumber,
  hasMore,
  onPageChange,
}: {
  pageNumber: number;
  hasMore: boolean;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="d-flex align-items-center gap-2 my-2">
      <Button
        variant="secondary"
        size="sm"
        disabled={pageNumber <= 1}
        onClick={() => onPageChange(pageNumber - 1)}
      >
        Previous page
      </Button>
      <span>Page {pageNumber}</span>
      <Button
        variant="secondary"
        size="sm"
        disabled={!hasMore}
        onClick={() => onPageChange(pageNumber + 1)}
      >
        Next page
      </Button>
    </div>
  );
}
